import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  accountQuery,
  getAccountLanguages,
  getDefaultAccountLanguage,
  translationsQuery,
  type AccountLanguage,
  type TranslationsResponse,
} from '@/api';
import { i18n } from './index';

const LANGUAGE_STORAGE_KEY = 'language';
const TRANSLATIONS_CACHE_KEY = 'translations-cache';

type TranslationNamespace = 'translation' | 'market' | 'outcome' | 'sports';

interface TranslationsContextValue {
  languages: AccountLanguage[];
  selectedLanguage: string;
  setSelectedLanguage: (language: string) => void;
  isLoadingTranslations: boolean;
}

const TranslationsContext = createContext<TranslationsContextValue | null>(null);

function toNamespaceResources(payload: TranslationsResponse) {
  return {
    translation: payload.WebsiteTranslates ?? {},
    market: payload.MarketTranslates ?? {},
    outcome: payload.OutcomeTranslates ?? {},
    sports: payload.SportsTranslates ?? {},
  } satisfies Record<TranslationNamespace, Record<string, string>>;
}

function saveSelectedLanguage(language: string) {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Non-blocking best-effort persistence.
  }
}

function getStoredLanguage(): string | null {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored && stored.trim() ? stored.trim() : null;
  } catch {
    return null;
  }
}

type CachedTranslations = {
  language: string;
  resources: ReturnType<typeof toNamespaceResources>;
};

function addTranslationBundles(
  language: string,
  resources: ReturnType<typeof toNamespaceResources>,
): void {
  for (const [namespace, dictionary] of Object.entries(resources)) {
    i18n.addResourceBundle(language, namespace, dictionary, true, true);
  }
}

function saveCachedTranslations(cache: CachedTranslations): void {
  try {
    localStorage.setItem(TRANSLATIONS_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Quota or private mode — the network copy still applies.
  }
}

/**
 * Seed i18n with the last fetched bundle so returning visitors see translated
 * chrome on first paint instead of waiting for account → translations.
 */
function hydrateCachedTranslations(): void {
  try {
    const raw = localStorage.getItem(TRANSLATIONS_CACHE_KEY);
    if (!raw) return;
    const cache = JSON.parse(raw) as Partial<CachedTranslations>;
    if (!cache.language || !cache.resources) return;
    addTranslationBundles(cache.language, cache.resources);
    if (getStoredLanguage() === cache.language) void i18n.changeLanguage(cache.language);
  } catch {
    // Corrupt cache — ignore and fetch fresh.
  }
}

export function TranslationsProvider({ children }: PropsWithChildren) {
  const { data: account } = useQuery(accountQuery());
  const languages = useMemo(() => getAccountLanguages(account), [account]);
  const defaultLanguage = useMemo(() => getDefaultAccountLanguage(account), [account]);

  const [selectedLanguageOverride, setSelectedLanguageState] = useState(() => {
    hydrateCachedTranslations();
    return getStoredLanguage();
  });
  const selectedLanguage = selectedLanguageOverride ?? defaultLanguage;

  useEffect(() => {
    if (
      selectedLanguage &&
      i18n.language !== selectedLanguage &&
      i18n.hasResourceBundle(selectedLanguage, 'translation')
    ) {
      void i18n.changeLanguage(selectedLanguage);
    }
  }, [selectedLanguage]);

  const setSelectedLanguage = useCallback((language: string) => {
    const nextLanguage = language.trim();
    if (!nextLanguage) return;
    setSelectedLanguageState(nextLanguage);
    saveSelectedLanguage(nextLanguage);
  }, []);

  const { data: translations, isFetching: isLoadingTranslations } = useQuery({
    ...translationsQuery(account?.Guid ?? '', selectedLanguage),
    enabled: Boolean(account?.Guid) && Boolean(selectedLanguage),
  });

  useEffect(() => {
    if (!translations || !selectedLanguage) return;

    const resources = toNamespaceResources(translations);
    addTranslationBundles(selectedLanguage, resources);
    saveCachedTranslations({ language: selectedLanguage, resources });

    void i18n.changeLanguage(selectedLanguage);
  }, [selectedLanguage, translations]);

  const value = useMemo<TranslationsContextValue>(
    () => ({
      languages,
      selectedLanguage,
      setSelectedLanguage,
      isLoadingTranslations,
    }),
    [isLoadingTranslations, languages, selectedLanguage, setSelectedLanguage],
  );

  return <TranslationsContext.Provider value={value}>{children}</TranslationsContext.Provider>;
}

export function useTranslations() {
  const context = useContext(TranslationsContext);
  if (!context) {
    throw new Error('useTranslations must be used within a TranslationsProvider');
  }
  return context;
}
